import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  MotifPagination,
  MotifPaginationSelect,
  MotifPaginationSelectItem,
  MotifTable,
  MotifToast,
} from '@ey-xd/motif-react';
import PublishDataCheckboxHeaderRenderer from './customRenderers/PublishTestResultCheckboxHeaderRenderer';
import PublishDataCheckboxCellRenderer from './customRenderers/PublishTestResultCheckboxCellRenderer';
import PublishPsqCheckboxHeaderRenderer from './customRenderers/PublishPsqCheckboxHeaderRenderer';
import PublishPsqCheckboxCellRenderer from './customRenderers/PublishPsqCheckboxCellRenderer';
import PublishArqcCheckboxHeaderRenderer from './customRenderers/PublishArqcCheckboxHeaderRenderer';
import PublishArqcCheckboxCellRenderer from './customRenderers/PublishArqcCheckboxCellRenderer';
import CustomHeaderTemplate from '../shared/customHeaders/CustomHeaderTemplate';
import GroupHeaderRenderer from '../shared/customHeaders/GroupHeaderRenderer';

/**
 * PublishDataTable component for displaying publish data grid
 * @param {Object} props Component props
 */
const PublishDataTable = ({ publishData, handleSelectionChange }) => {
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const rowDataRef = useRef([]);
  rowDataRef.current = rowData;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);

  // Default column settings
  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      minWidth: 120,
      filter: true,
      resizable: true,
      sortable: true,
      wrapText: true,
      autoHeight: true,
      headerClass: 'ag-center-header',
    };
  }, []);

  const onHeaderCheckboxChange = (isHeaderCheckboxSelected) => {
    const api = gridRef && gridRef.current ? gridRef.current.api : null;
    if (!api) return;
    const toRefresh = [];
    api.forEachNodeAfterFilterAndSort((rowNode) => {
      if (
        rowNode.level === 0 &&
        rowNode.data?.isTestResultsServiceScopeEnabled
      ) {
        rowNode.setDataValue(
          'isTestResultsDataPublished',
          !isHeaderCheckboxSelected
        );
        toRefresh.push(rowNode);
      }
    });
    if (toRefresh.length) {
      api.refreshCells({
        rowNodes: toRefresh,
        columns: ['isTestResultsDataPublished'],
        force: true,
      });
      api.refreshHeader();
      handleSelectionChange(rowDataRef.current);
    }
  };

  const onCellCheckboxChange = (id, rowNode) => {
    // Targeted update to avoid grid/header refresh flicker
    const api = gridRef && gridRef.current ? gridRef.current.api : null;
    if (api) {
      let targetNode = rowNode || null;
      if (!targetNode) {
        api.forEachNodeAfterFilterAndSort((n) => {
          if (
            !targetNode &&
            (n?.data?.id === id || n?.data?.reportPeriodID === id)
          )
            targetNode = n;
        });
      }
      if (targetNode) {
        const current = !!targetNode.data.isTestResultsDataPublished;
        targetNode.setDataValue('isTestResultsDataPublished', !current);
        api.refreshCells({
          rowNodes: [targetNode],
          columns: ['isTestResultsDataPublished'],
          force: true,
        });
        api.refreshHeader();
        handleSelectionChange(rowDataRef.current);
        return;
      }
    }
    const updatedData = rowDataRef.current.map((ud) => {
      if (ud.id === id) {
        return {
          ...ud,
          isTestResultsDataPublished: !ud.isTestResultsDataPublished,
        };
      }
      return ud;
    });
    handleSelectionChange(updatedData);
    setRowData(updatedData);
  };

  // PSQ selection handlers
  const onHeaderCheckboxChangePsq = (isHeaderCheckboxSelected) => {
    const api = gridRef && gridRef.current ? gridRef.current.api : null;
    if (!api) return;
    const toRefresh = [];
    api.forEachNodeAfterFilterAndSort((rowNode) => {
      const eligible =
        rowNode.data?.isPSQServiceScopeEnabled ||
        rowNode.data?.isPSQDataPublished;
      if (rowNode.level === 0 && eligible) {
        rowNode.setDataValue('isPSQDataPublished', !isHeaderCheckboxSelected);
        toRefresh.push(rowNode);
      }
    });
    if (toRefresh.length) {
      api.refreshCells({
        rowNodes: toRefresh,
        columns: ['isPSQDataPublished'],
        force: true,
      });
      api.refreshHeader();
      handleSelectionChange(rowDataRef.current);
    }
  };

  const onCellCheckboxChangePsq = (id, rowNode) => {
    const api = gridRef && gridRef.current ? gridRef.current.api : null;
    if (api) {
      let targetNode = rowNode || null;
      if (!targetNode) {
        api.forEachNodeAfterFilterAndSort((n) => {
          if (!targetNode && n?.data?.id === id) targetNode = n;
        });
      }
      if (targetNode) {
        const current = !!targetNode.data.isPSQDataPublished;
        targetNode.setDataValue('isPSQDataPublished', !current);
        api.refreshCells({
          rowNodes: [targetNode],
          columns: ['isPSQDataPublished'],
          force: true,
        });
        api.refreshHeader();
        handleSelectionChange(rowDataRef.current);
        return;
      }
    }
    const updatedData = rowDataRef.current.map((ud) => {
      if (ud.id === id) {
        return { ...ud, isPSQDataPublished: !ud.isPSQDataPublished };
      }
      return ud;
    });
    handleSelectionChange(updatedData);
    setRowData(updatedData);
  };

  // ARQC selection handlers
  const onHeaderCheckboxChangeArqc = (isHeaderCheckboxSelected) => {
    const api = gridRef && gridRef.current ? gridRef.current.api : null;
    if (!api) return;
    const toRefresh = [];
    api.forEachNodeAfterFilterAndSort((rowNode) => {
      const eligible =
        rowNode.data?.isARQCServiceScopeEnabled ||
        rowNode.data?.isARQCDataPublished;
      if (rowNode.level === 0 && eligible) {
        rowNode.setDataValue('isARQCDataPublished', !isHeaderCheckboxSelected);
        toRefresh.push(rowNode);
      }
    });
    if (toRefresh.length) {
      api.refreshCells({
        rowNodes: toRefresh,
        columns: ['isARQCDataPublished'],
        force: true,
      });
      api.refreshHeader();
      handleSelectionChange(rowDataRef.current);
    }
  };

  const onCellCheckboxChangeArqc = (id, rowNode) => {
    const api = gridRef && gridRef.current ? gridRef.current.api : null;
    if (api) {
      let targetNode = rowNode || null;
      if (!targetNode) {
        api.forEachNodeAfterFilterAndSort((n) => {
          if (!targetNode && n?.data?.id === id) targetNode = n;
        });
      }
      if (targetNode) {
        const current = !!targetNode.data.isARQCDataPublished;
        targetNode.setDataValue('isARQCDataPublished', !current);
        api.refreshCells({
          rowNodes: [targetNode],
          columns: ['isARQCDataPublished'],
          force: true,
        });
        api.refreshHeader();
        handleSelectionChange(rowDataRef.current);
        return;
      }
    }
    const updatedData = rowDataRef.current.map((ud) => {
      if (ud.id === id) {
        return { ...ud, isARQCDataPublished: !ud.isARQCDataPublished };
      }
      return ud;
    });
    handleSelectionChange(updatedData);
    setRowData(updatedData);
  };

  // MM:DD:YYYY
  const dateFormat = {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  };

  const getColumnDefs = () => {
    return [
      {
        field: 'reitName',
        headerName: 'REIT',
        sortable: true,
        minWidth: 180,
        headerComponentParams: { template: CustomHeaderTemplate('REIT') },
      },
      {
        field: 'TestResults',
        headerName: 'Test Results',
        marryChildren: true,
        headerClass: 'ag-center-header',
        headerGroupComponent: GroupHeaderRenderer,
        headerGroupComponentParams: { dataTestId: 'TestResults' },
        children: [
          {
            field: 'isTestResultsDataPublished',
            headerName: '',
            headerComponent: PublishDataCheckboxHeaderRenderer,
            headerComponentParams: {
              onHeaderCheckboxChange: onHeaderCheckboxChange,
              template: CustomHeaderTemplate('PublishedTestResults'),
            },
            headerClass: 'no-right-border',
            minWidth: 45,
            maxWidth: 55,
            cellRenderer: PublishDataCheckboxCellRenderer,
            cellRendererParams: { onCellCheckboxChange: onCellCheckboxChange },
            comparator: (a, b, nodeA, nodeB) => {
              // Keep original sorting behavior aligned with published flag if needed
              const pa = nodeA?.data?.isTestResultsServiceScopeEnabled ? 1 : 0;
              const pb = nodeB?.data?.isTestResultsServiceScopeEnabled ? 1 : 0;
              if (pa !== pb) return pb - pa; // published rows first
              return a === b ? 0 : a ? -1 : 1;
            },
            cellStyle: (params) => {
              const eligible =
                params.data.isTestResultsServiceScopeEnabled ||
                params.data.isTestResultsDataPublished;
              return !eligible
                ? { 'pointer-events': 'none', opacity: '0.3' }
                : { textAlign: 'center', paddingTop: '6px' };
            },
          },
          {
            field: 'PublishedTestResults',
            headerName: 'Published',
            sortable: true,
            filter: true,
            minWidth: 150,
            maxWidth: 170,
            headerComponentParams: {
              template: CustomHeaderTemplate('PublishedTestResults'),
            },
          },
          {
            field: 'testResultsDataPublishedBy',
            headerName: 'Published By',
            sortable: true,
            minWidth: 170,
            maxWidth: 190,
            cellStyle: { paddingTop: '12px', textAlign: 'center' },
            headerComponentParams: {
              template: CustomHeaderTemplate('PublishedByTestResults'),
            },
          },
          {
            field: 'testResultsDataPublishedOn',
            headerName: 'Published Date',
            sortable: true,
            minWidth: 180,
            maxWidth: 200,
            headerComponentParams: {
              template: CustomHeaderTemplate('PublishedDateTestResults'),
            },
            valueGetter: (params) => {
              const dateValue =
                params?.data?.testResultsDataPublishedOn ?? null;
              if (dateValue) {
                const date = new Date(dateValue);
                // use toLocaleString with undefined value to get the local time zone settings
                const formattedDate = new Date(date + 'Z').toLocaleString(
                  undefined,
                  dateFormat
                );
                return `${formattedDate}`;
              }
              return '';
            },
          },
        ],
      },
      {
        headerName: 'PSQ',
        marryChildren: true,
        headerClass: 'ag-center-header',
        headerGroupComponent: GroupHeaderRenderer,
        headerGroupComponentParams: { dataTestId: 'Psq' },
        children: [
          {
            field: 'isPSQDataPublished',
            headerName: '',
            headerComponent: PublishPsqCheckboxHeaderRenderer,
            headerComponentParams: {
              onHeaderCheckboxChange: onHeaderCheckboxChangePsq,
              template: CustomHeaderTemplate('Published'),
            },
            headerClass: 'no-right-border',
            minWidth: 45,
            maxWidth: 55,
            cellRenderer: PublishPsqCheckboxCellRenderer,
            cellRendererParams: {
              onCellCheckboxChange: onCellCheckboxChangePsq,
            },
            comparator: (a, b, nodeA, nodeB) => {
              const pa = nodeA?.data?.isPSQServiceScopeEnabled ? 1 : 0;
              const pb = nodeB?.data?.isPSQServiceScopeEnabled ? 1 : 0;
              if (pa !== pb) return pb - pa;
              return a === b ? 0 : a ? -1 : 1;
            },
            cellStyle: (params) => {
              const eligible =
                params.data.isPSQServiceScopeEnabled ||
                params.data.isPSQDataPublished;
              return !eligible
                ? { 'pointer-events': 'none', opacity: '0.3' }
                : { textAlign: 'center', paddingTop: '6px' };
            },
          },
          {
            field: 'PublishedPsq',
            headerName: 'Published',
            sortable: true,
            filter: true,
            minWidth: 150,
            maxWidth: 170,
            headerComponentParams: {
              template: CustomHeaderTemplate('PublishedPsq'),
            },
          },
          {
            field: 'psqDataPublishedBy',
            headerName: 'Published By',
            sortable: true,
            minWidth: 170,
            maxWidth: 190,
            headerComponentParams: {
              template: CustomHeaderTemplate('PublishedByPsq'),
            },
          },
          {
            field: 'psqDataPublishedOn',
            headerName: 'Published Date',
            sortable: true,
            minWidth: 180,
            maxWidth: 200,
            headerComponentParams: {
              template: CustomHeaderTemplate('PublishedDatePsq'),
            },
            valueGetter: (params) => {
              const dateValue = params.data
                ? params.data.psqDataPublishedOn
                : null;
              if (dateValue) {
                const date = new Date(dateValue);
                // use toLocaleString with undefined value to get the local time zone settings
                const formattedDate = new Date(date + 'Z').toLocaleString(
                  undefined,
                  dateFormat
                );
                return `${formattedDate}`;
              }
              return '';
            },
          },
        ],
      },
      {
        headerName: 'ARQC',
        marryChildren: true,
        headerGroupComponent: GroupHeaderRenderer,
        headerGroupComponentParams: { dataTestId: 'Arqc' },
        headerClass: 'ag-center-header',
        children: [
          {
            field: 'isARQCDataPublished',
            headerName: '',
            headerComponent: PublishArqcCheckboxHeaderRenderer,
            headerComponentParams: {
              onHeaderCheckboxChange: onHeaderCheckboxChangeArqc,
              template: CustomHeaderTemplate('Published'),
            },
            headerClass: 'no-right-border',
            minWidth: 45,
            maxWidth: 55,
            cellRenderer: PublishArqcCheckboxCellRenderer,
            cellRendererParams: {
              onCellCheckboxChange: onCellCheckboxChangeArqc,
            },
            comparator: (a, b, nodeA, nodeB) => {
              const pa = nodeA?.data?.isARQCServiceScopeEnabled ? 1 : 0;
              const pb = nodeB?.data?.isARQCServiceScopeEnabled ? 1 : 0;
              if (pa !== pb) return pb - pa;
              return a === b ? 0 : a ? -1 : 1;
            },
            cellStyle: (params) => {
              const eligible =
                params.data.isARQCServiceScopeEnabled ||
                params.data.isARQCDataPublished;
              return !eligible
                ? { 'pointer-events': 'none', opacity: '0.3' }
                : { textAlign: 'center', paddingTop: '6px' };
            },
          },
          {
            field: 'PublishedArqc',
            headerName: 'Published',
            sortable: true,
            filter: true,
            minWidth: 150,
            maxWidth: 170,
            headerComponentParams: {
              template: CustomHeaderTemplate('PublishedArqc'),
            },
          },
          {
            field: 'arqcDataPublishedBy',
            headerName: 'Published By',
            sortable: true,
            minWidth: 170,
            maxWidth: 190,
            headerComponentParams: {
              template: CustomHeaderTemplate('PublishedByArqc'),
            },
          },
          {
            field: 'arqcDataPublishedOn',
            headerName: 'Published Date',
            sortable: true,
            minWidth: 180,
            maxWidth: 200,
            headerComponentParams: {
              template: CustomHeaderTemplate('PublishedDateArqc'),
            },
            valueGetter: (params) => {
              const dateValue = params.data
                ? params.data.arqcDataPublishedOn
                : null;
              if (dateValue) {
                const date = new Date(dateValue);
                // use toLocaleString with undefined value to get the local time zone settings
                const formattedDate = new Date(date + 'Z').toLocaleString(
                  undefined,
                  dateFormat
                );
                return `${formattedDate}`;
              }
              return '';
            },
          },
        ],
      },
    ];
  };

  const [columnDefs] = useState(getColumnDefs());

  // Handle Grid Ready event
  const handleGridReady = (event) => {
    // Remove and add global listener like diagnostic table
    if (event && event.api) {
      event.api.removeEventListener(gridGlobalListner);
      event.api.addGlobalListener(gridGlobalListner);
    }
    // Set the page size and total pages on the first load
    handleItemsPerPageChange(100);
  };

  //Grid Global Listner
  const gridGlobalListner = function (type, event) {
    if (type.indexOf('columnVisible') >= 0) {
      handleGridColumnsChanged(event);
    }

    // Bind handleFilterChanged event to filterChanged and rowDataUpdated events
    if (type.indexOf('filterChanged') >= 0) {
      setTimeout(function () {
        handleFilterChanged(event);
      }, 0);
    }

    if (type.indexOf('rowDataUpdated') >= 0) {
      setTimeout(function () {
        handleFilterChanged(event);
      }, 0);
    }
  };

  // Handle OnFilterChanged event
  const handleFilterChanged = () => {
    if (gridRef && gridRef.current && gridRef.current.api) {
      let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
      if (currentPage > calcTotalPages) {
        if (calcTotalPages == 0) {
          setTimeout(function () {
            setCurrentPage(calcTotalPages);
          }, 0);
        } else {
          setCurrentPage(calcTotalPages);
        }
      }
      const gridCurrentPage =
        gridRef.current.api.paginationGetCurrentPage() + 1;
      if (currentPage < gridCurrentPage && calcTotalPages != 0) {
        setCurrentPage(gridCurrentPage);
      }

      if (currentPage <= 1 && gridCurrentPage == 1 && calcTotalPages != 0) {
        setCurrentPage(gridCurrentPage);
      }
    }
  };

  // Handle First Data Rendered event
  const handleFirstDataRendered = (params) => {
    // Prefer the event params' columnApi; fallback to gridRef if available
    const columnApi =
      params && params.columnApi
        ? params.columnApi
        : gridRef && gridRef.current && gridRef.current.columnApi
          ? gridRef.current.columnApi
          : null;
    if (columnApi && typeof columnApi.autoSizeAllColumns === 'function') {
      columnApi.autoSizeAllColumns();
    }
  };

  // Handle Grid Columns changed event (simplified to auto-size columns)
  const handleGridColumnsChanged = (event) => {
    const columnApi =
      gridRef && gridRef.current && gridRef.current.columnApi
        ? gridRef.current.columnApi
        : event && event.columnApi
          ? event.columnApi
          : null;
    if (columnApi && typeof columnApi.autoSizeAllColumns === 'function') {
      columnApi.autoSizeAllColumns();
    }
  };

  // Handle Grid Items per Page change event
  const handleItemsPerPageChange = (val) => {
    gridRef.current.api.paginationGoToFirstPage();
    gridRef.current.api.setGridOption('paginationPageSize', Number(val));
    setCurrentPage(1);
    setItemsPerPage(val);
    if (gridRef && gridRef.current && gridRef.current.api) {
      let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
      if (calcTotalPages === 0) {
        setCurrentPage(calcTotalPages);
      }
    }
  };

  // Handle Grid Page change event
  const handlePageChange = (val) => {
    setCurrentPage(val);
    gridRef.current.api.paginationGoToPage(val - 1);
  };

  useEffect(() => {
    const normalized = (publishData || []).map((row) => ({
      ...row,
      isTestResultsDataPublished:
        typeof row.isTestResultsDataPublished === 'boolean'
          ? row.isTestResultsDataPublished
          : false,
      isPSQDataPublished:
        typeof row.isPSQDataPublished === 'boolean'
          ? row.isPSQDataPublished
          : false,
      isARQCDataPublished:
        typeof row.isARQCDataPublished === 'boolean'
          ? row.isARQCDataPublished
          : false,
    }));
    setRowData(normalized);

    if (gridRef && gridRef.current && gridRef.current.api) {
      // redraw rows then refresh header so header checkbox renderers re-evaluate
      gridRef.current.api.redrawRows();
      gridRef.current.api.refreshHeader();
      let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
    }
  }, [publishData]);

  return (
    <>
      <div>
        <div className="row mb-10">
          {publishData && publishData.length > 0 && (
            <div className="col mt-3">
              <div
                className="ag-theme-quartz motif-table table-bd-full-height-width"
                data-testid="grdPublishData"
              >
                <MotifTable
                  zebra={true}
                  compact={true}
                  rowData={rowData}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  accentedSort={true}
                  onGridReady={handleGridReady}
                  onFirstDataRendered={handleFirstDataRendered}
                  ref={gridRef}
                  pagination={true}
                  gridOptions={{ suppressPaginationPanel: true }}
                  data-testid="grdPublishData"
                  onFilterChanged={handleFilterChanged}
                />
                <div className="mt-3">
                  <span
                    className="left col-lg-9"
                    style={{ minWidth: '0', marginLeft: '-14px' }}
                  >
                    <MotifToast
                      className="mb-5 removePadding"
                      data-testid="eyToastMsg"
                      hideclosebutton="true"
                    >
                      External users and the GTP-FS team members will only see
                      data that is published for the selected report period in
                      Analytics Reports. Unpublished data will not be visible.
                    </MotifToast>
                  </span>

                  <span className="right mb-5">
                    <MotifPagination
                      currentPage={currentPage}
                      onPageChange={handlePageChange}
                      min={1}
                      max={totalPages}
                    >
                      <MotifPaginationSelect
                        data-testid="itemsPerPage"
                        aria-label={`Items per page ${itemsPerPage}`}
                      >
                        <MotifPaginationSelectItem
                          onClick={() => handleItemsPerPageChange(50)}
                        >
                          Show 50
                        </MotifPaginationSelectItem>
                        <MotifPaginationSelectItem
                          selected
                          onClick={() => handleItemsPerPageChange(100)}
                        >
                          Show 100
                        </MotifPaginationSelectItem>
                        <MotifPaginationSelectItem
                          onClick={() => handleItemsPerPageChange(150)}
                        >
                          Show 150
                        </MotifPaginationSelectItem>
                        <MotifPaginationSelectItem
                          onClick={() => handleItemsPerPageChange(200)}
                        >
                          Show 200
                        </MotifPaginationSelectItem>
                      </MotifPaginationSelect>
                    </MotifPagination>
                  </span>
                </div>
              </div>
            </div>
          )}
          {(!publishData || publishData.length === 0) && (
            <div
              className="standardSpacingT12R12B16L16"
              data-testid="noActiveReitsMessage"
            >
              No active REITs found for the selected report period.
            </div>
          )}
        </div>
      </div>
    </>
  );
};

PublishDataTable.propTypes = {
  publishData: PropTypes.array.isRequired,
  handleSelectionChange: PropTypes.func.isRequired,
};

export default PublishDataTable;
